import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConsignmentLineItem } from '@/entities/consignments';
import type { LocalPallet } from '../model/types';
import { calculateTotals } from '../lib/calculate-units';
import { useMemo } from 'react';

interface PalletHeaderProps {
  pallet: LocalPallet;
  lines: ConsignmentLineItem[];
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

export function PalletHeader({ pallet, lines, isExpanded, onToggle, onDelete }: PalletHeaderProps) {
  // Prefer the backend-computed load (correct for lines split across pallets);
  // fall back to client-side totals on older backends that don't return it.
  const fallback = useMemo(() => calculateTotals(lines), [lines]);
  const totalWeight = pallet.currentWeight ?? fallback.totalWeight;
  const totalVolume = pallet.currentVolume ?? fallback.totalVolume;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border-y hover:bg-muted/60 transition-colors">
      <button
        type="button"
        className="flex items-center gap-2 flex-1 text-left cursor-pointer"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <span className="font-semibold text-sm">{pallet.type || 'Pallet'}</span>
        <span className="text-sm">
          {pallet.palletNumber} | Weight: {totalWeight.toFixed(2)}
          {pallet.maxWeight != null ? ` / ${pallet.maxWeight}` : ''} kg | Volume: {totalVolume.toFixed(4)}
          {pallet.maxVolume != null ? ` / ${pallet.maxVolume}` : ''} m³
        </span>
        <span className="text-xs text-muted-foreground ml-auto">
          {lines.length} line{lines.length !== 1 ? 's' : ''}
        </span>
      </button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete pallet">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
